/**
 * v-gtm-events.js — GA4 global event tracking + CookieYes consent integration
 *
 * Exposes window.vGtm API and auto-binds site-wide GA4 events:
 *   - CookieYes consent update listener
 *   - scroll (25 / 50 / 75 / 90)
 *   - navigation_bar_click (header nav)
 *   - bottom_navigation (footer links)
 *   - follow_us (footer social)
 *   - email_signup (footer newsletter)
 *   - email_signup (newsletter popup — #10)
 *   - select_other_brand (footer brand links)
 *   - follow_us (waitlist popup social — #12)
 *   - form_submit (contact + influencer forms — #47 / #54)
 *   - search (newsroom hero + list — #51)
 *   - banner_click (About Us / Experience / Influencer — #35 / #38 / #48)
 *   - button_click (Experience brands — #39)
 *   - app_download (app store badges — #40 / #41)
 *   - contact_us (Contact Us page mailto — #45)
 *   - button_click (404 page)
 *
 * Future page-level events use:  window.vGtm.push('event_name', { ... })
 */
(function () {
  'use strict';

  var DL = (window.dataLayer = window.dataLayer || []);
  var debug = new URLSearchParams(location.search).has('vgtm_debug');

  /* ================================================================
     Global API — window.vGtm
     ================================================================ */

  function push(eventName, params) {
    DL.push({ event_parameters: null });
    DL.push({
      event: 'ga4Event',
      event_name: eventName,
      event_parameters: params || {}
    });
    if (debug) console.log('[vGtm]', eventName, params);
  }

  function pushRaw(obj) {
    DL.push(obj);
    if (debug) console.log('[vGtm:raw]', obj);
  }

  function updateConsent(state) {
    if (typeof gtag !== 'function') return;
    gtag('consent', 'update', state);
    if (debug) console.log('[vGtm:consent]', state);
  }

  window.vGtm = { push: push, pushRaw: pushRaw, updateConsent: updateConsent, debug: debug };

  /* ================================================================
     CookieYes Consent Listener
     ================================================================ */

  function mapCkyConsent(detail) {
    if (!detail) return;
    var ad = detail.advertisement === 'yes' ? 'granted' : 'denied';
    var analytics =
      detail.analytics === 'yes' || detail.performance === 'yes'
        ? 'granted'
        : 'denied';
    updateConsent({
      ad_storage: ad,
      ad_user_data: ad,
      ad_personalization: ad,
      analytics_storage: analytics
    });
  }

  document.addEventListener('cookieyes_consent_update', function (e) {
    mapCkyConsent(e.detail || {});
  });

  try {
    if (typeof getCkyConsent === 'function') {
      var initial = getCkyConsent();
      if (initial) mapCkyConsent(initial);
    }
  } catch (_) {
    /* CookieYes not yet loaded */
  }

  /* ================================================================
     Helpers
     ================================================================ */

  function txt(el) {
    return (el.getAttribute('data-v-gtm-text') || el.textContent || '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function gtmAttr(el, key) {
    return (el.getAttribute('data-v-gtm-' + key) || '').trim();
  }

  function socialPlatform(href) {
    if (!href) return '';
    try {
      var h = new URL(href).hostname.toLowerCase();
      var map = {
        'facebook': 'facebook', 'fb.com': 'facebook',
        'instagram': 'instagram', 'youtube': 'youtube',
        'tiktok': 'tiktok', 'x.com': 'x', 'twitter': 'twitter',
        'pinterest': 'pinterest', 'linkedin': 'linkedin',
        'snapchat': 'snapchat', 'vimeo': 'vimeo',
        'threads': 'threads', 'whatsapp': 'whatsapp',
        'tumblr': 'tumblr', 'wechat': 'wechat',
        'reddit': 'reddit', 'line': 'line', 'spotify': 'spotify'
      };
      var keys = Object.keys(map);
      for (var i = 0; i < keys.length; i++) {
        if (h.indexOf(keys[i]) !== -1) return map[keys[i]];
      }
      return h.replace(/^www\./, '').split('.')[0];
    } catch (_) {
      return '';
    }
  }

  /* ================================================================
     Scroll Depth Tracking — 25 / 50 / 75 / 90
     ================================================================ */

  var scrollThresholds = [25, 50, 75, 90];
  var scrollFired = {};
  var scrollTicking = false;

  function checkScroll() {
    var docH = document.documentElement.scrollHeight - window.innerHeight;
    if (docH <= 0) return;
    var pct = Math.round((window.scrollY / docH) * 100);
    for (var i = 0; i < scrollThresholds.length; i++) {
      var t = scrollThresholds[i];
      if (pct >= t && !scrollFired[t]) {
        scrollFired[t] = true;
        push('scroll', { percent_scrolled: String(t) });
      }
    }
  }

  window.addEventListener(
    'scroll',
    function () {
      if (!scrollTicking) {
        scrollTicking = true;
        requestAnimationFrame(function () {
          checkScroll();
          scrollTicking = false;
        });
      }
    },
    { passive: true }
  );

  /* ================================================================
     Header — navigation_bar_click (#5)
     ================================================================ */

  function handleHeaderNav(target) {
    var url = target.href || target.getAttribute('data-url') || '';
    if (!url || url === '#') return;

    var linkText = gtmAttr(target, 'text') || txt(target);
    var navCategory = '';
    var navSub = '';
    var navSubSub = '';

    var panel = target.closest(
      '.mega-menu, .dropdown-menu, .v-mega-dropdown, navigation-drawer, .navigation-drawer'
    );

    if (panel) {
      var topDetails = panel.closest('details');
      if (topDetails) {
        var summary = topDetails.querySelector(':scope > summary');
        if (summary) navCategory = txt(summary);
      }

      var nestedDetails = target.closest('details');
      if (nestedDetails && nestedDetails !== topDetails) {
        var nestedSummary = nestedDetails.querySelector(':scope > summary');
        if (nestedSummary) navSub = txt(nestedSummary);
        navSubSub = linkText;
      } else {
        navSub = linkText;
      }
    } else if (target.closest('navigation-drawer, .navigation-drawer')) {
      navCategory = linkText;
    } else {
      navCategory = linkText;
    }

    push('navigation_bar_click', {
      link_url: url,
      link_text: linkText,
      navigation_category: gtmAttr(target, 'nav-category') || navCategory,
      navigation_sub_category: gtmAttr(target, 'nav-sub') || navSub,
      navigation_sub_sub_category: gtmAttr(target, 'nav-sub-sub') || navSubSub
    });
  }

  /* ================================================================
     Footer — bottom_navigation (#6)
     ================================================================ */

  function handleFooterNav(target) {
    var column = target.closest('.v-footer__column');
    var groupTitle = '';
    if (column) {
      var toggle = column.querySelector('.v-footer__link-toggle');
      if (toggle) groupTitle = txt(toggle);
    }
    push('bottom_navigation', {
      navigation_category: gtmAttr(target, 'nav-category') || groupTitle,
      navigation_sub_category: gtmAttr(target, 'nav-sub') || txt(target)
    });
  }

  /* ================================================================
     Footer — follow_us (#7)
     ================================================================ */

  function handleSocialClick(target) {
    var platform =
      gtmAttr(target, 'button-name') || socialPlatform(target.href);
    var list = target.closest('ul.social-media');
    var waitlistModal = target.closest('v-waitlist-modal, .v-waitlist-modal');
    var module = '';
    if (waitlistModal) {
      module = 'Popup_Join the Waitlist';
    } else {
      module =
        gtmAttr(target, 'module') ||
        (list && list.getAttribute('data-v-gtm-module')) ||
        'Bottom Function';
    }
    push('follow_us', {
      module: module,
      button_name: platform
    });
  }

  /* ================================================================
     Footer — select_other_brand (#9)
     ================================================================ */

  function handleBrandClick(target) {
    push('select_other_brand', {
      link_url: target.href || '',
      button_name: gtmAttr(target, 'button-name') || txt(target)
    });
  }

  /* ================================================================
     404 — button_click (#47)
     ================================================================ */

  function handle404Click(target) {
    var section = target.closest(
      '.shopify-section--404, .not-found, [class*="v-404"]'
    );
    var heading =
      section &&
      section.querySelector('.section-header__heading, h1, h2, h3');
    push('button_click', {
      module: gtmAttr(target, 'module') || (heading ? txt(heading) : '404'),
      button_name: gtmAttr(target, 'button-name') || txt(target)
    });
  }

  /* ================================================================
     Inferred banner_click — page templates without gtm_event in JSON (#35 / #38 / #48)
     ================================================================ */

  function bannerNameFromContainer(container) {
    if (!container) return '';
    var heading = container.querySelector(
      '.h0, .h1, .h2, h1, h2, [class*="heading"], .prose .h1, .prose .h2'
    );
    return heading ? txt(heading) : '';
  }

  function isHealthBenefitsPage() {
    var body = document.body;
    return (
      body.classList.contains('template-page-womens-health') ||
      body.classList.contains('template-page-stress') ||
      body.classList.contains('template-page-sleep-and-circadian-rhythm') ||
      body.classList.contains('template-page-heart-rate-hrv') ||
      body.classList.contains('template-page-energy-and-oxygen-level')
    );
  }

  function handleHealthBenefitBannerClick(target, container) {
    handleInferredBannerClick(target, container);
  }

  function handleInferredBannerClick(target, container) {
    push('banner_click', {
      banner_location: 'hero_banner',
      banner_type: 'promotional',
      banner_name: bannerNameFromContainer(container),
      button_name: txt(target)
    });
  }

  function handleExperienceBrandClick(target) {
    var item = target.closest('.v-experience-brands__item');
    var headingEl =
      item && item.querySelector('.v-experience-brands__heading');
    push('button_click', {
      module: headingEl ? txt(headingEl) : 'Experience',
      button_name: txt(target)
    });
  }

  function isOverlayBannerButton(target) {
    return (
      target.matches(
        'a.button, button.button, a[class*="button"], .button[href]'
      ) || !!target.closest('a.button, button.button')
    );
  }

  function handleAppDownloadClick(target) {
    var appBtn = target.closest('.v-app-download-button');
    if (!appBtn) return false;
    var section = appBtn.closest(
      '.shopify-section--v-the-app-hero, .shopify-section--v-the-app-better-together, .shopify-section--v-app-phone-features, .shopify-section--v-experience-app, [class*="v-the-app"]'
    );
    var moduleName = 'The app';
    if (section) {
      var heading = section.querySelector(
        '.h0, .h1, .h2, h1, h2, [class*="heading"]'
      );
      if (heading) moduleName = txt(heading);
    }
    push('app_download', {
      module: moduleName,
      button_name: appBtn.getAttribute('aria-label') || txt(appBtn)
    });
    return true;
  }

  function handleContactUsClick(target) {
    if (!document.body.classList.contains('template-page-contact-us')) return false;
    var mailLink = target.closest(
      '.v-contact-us-form a[href^="mailto:"], .shopify-section--v-multi-column a[href^="mailto:"]'
    );
    if (!mailLink) return false;
    var card = mailLink.closest('.multi-column__item, .v-multi-column__item');
    var titleEl =
      card &&
      card.querySelector('h3, h4, .h3, .h4, [class*="title"]');
    push('contact_us', {
      button_name: titleEl ? txt(titleEl) : txt(mailLink)
    });
    return true;
  }

  /* ================================================================
     Footer — email_signup (#8) — form submit
     ================================================================ */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches) return;

    /* --- Footer newsletter email_signup (#8) --- */
    if (form.matches('#footer-newsletter, .v-footer__newsletter-form')) {
      var submitBtn = form.querySelector('[type="submit"]');
      push('email_signup', {
        signup_type: 'Newsletter',
        product_name: 'undefined',
        module: gtmAttr(form, 'module') || 'Bottom Subscription',
        button_name:
          gtmAttr(form, 'button-name') ||
          (submitBtn ? txt(submitBtn) : 'Subscribe')
      });
      return;
    }

    /* --- Waitlist modal email_signup (#11) --- */
    if (form.matches('#v-waitlist-modal-form, .v-waitlist-modal__form')) {
      var wlSubmitBtn = form.querySelector('[type="submit"]');
      push('email_signup', {
        signup_type: gtmAttr(form, 'signup-type') || 'Waitlist',
        product_name: gtmAttr(form, 'product-name') || 'undefined',
        module: gtmAttr(form, 'module') || 'Middle_Popup_Join the Waitlist',
        button_name:
          gtmAttr(form, 'button-name') ||
          (wlSubmitBtn ? txt(wlSubmitBtn) : 'Subscribe')
      });
      return;
    }

    /* --- Newsletter popup email_signup (#10) --- */
    if (
      form.closest('.newsletter-drawer, #newsletter-drawer, newsletter-popup')
    ) {
      var popupSubmitBtn = form.querySelector('[type="submit"]');
      push('email_signup', {
        signup_type: 'Newsletter',
        product_name: 'undefined',
        module: 'Popup_Signup for our newsletter',
        button_name:
          gtmAttr(form, 'button-name') ||
          (popupSubmitBtn ? txt(popupSubmitBtn) : 'Subscribe')
      });
      return;
    }

    /* --- Contact Us form_submit (#44) --- */
    if (form.matches('.v-contact-form__form')) {
      var card = form.closest('.v-contact-form__card');
      var headingEl = card && card.querySelector('.v-contact-form__card-heading');
      var contactSubmitBtn = form.querySelector('[type="submit"]');
      push('form_submit', {
        form_name: headingEl ? txt(headingEl) : 'Get in Touch',
        form_submit_text: contactSubmitBtn ? txt(contactSubmitBtn) : 'Submit'
      });
      return;
    }

    /* --- Generic newsletter / waitlist email_signup --- */
    if (form.hasAttribute('data-v-gtm-signup-type')) {
      var genericSubmitBtn = form.querySelector('[type="submit"]');
      push('email_signup', {
        signup_type: gtmAttr(form, 'signup-type') || 'Waitlist',
        product_name: gtmAttr(form, 'product-name') || 'undefined',
        module: gtmAttr(form, 'module') || '',
        button_name:
          gtmAttr(form, 'button-name') ||
          (genericSubmitBtn ? txt(genericSubmitBtn) : 'Subscribe')
      });
    }
  });

  /* ================================================================
     Global Click Delegation
     ================================================================ */

  document.addEventListener(
    'click',
    function (e) {
      var target = e.target.closest('a[href], button, summary[data-url]');
      if (!target) return;

      /* --- Header navigation --- */
      var header = target.closest('store-header, .header');
      if (header) {
        if (
          target.closest(
            '.header__logo, [aria-controls="search-drawer"], .header__cart-icon, .header__cart-count'
          )
        )
          return;
        if (target.tagName === 'A' || target.hasAttribute('data-url')) {
          handleHeaderNav(target);
        }
        return;
      }

      /* --- Social media icons (global — footer, modals, any context) --- */
      if (target.closest('ul.social-media')) {
        if (target.tagName === 'A') handleSocialClick(target);
        return;
      }

      /* --- Footer --- */
      var footer = target.closest('.v-footer');
      if (footer) {
        if (target.closest('.v-footer__brand-links')) {
          handleBrandClick(target);
          return;
        }
        if (
          target.closest('.v-footer__link-list') ||
          target.closest('.v-footer__policy-links')
        ) {
          if (target.tagName === 'A') handleFooterNav(target);
          return;
        }
        return;
      }

      /* --- 404 page --- */
      if (document.body.classList.contains('template-404')) {
        if (
          target.matches(
            'a.button, button.button, [data-v-gtm-event="button_click"]'
          )
        ) {
          handle404Click(target);
          return;
        }
      }

      /* --- v-the-app-energy-tabs: tab_click (#20) --- */
      if (target.matches('.v-the-app-energy-tabs__tab')) {
        var energyTabs = target.closest('.v-the-app-energy-tabs');
        if (energyTabs) {
          var tabsHeading = energyTabs.querySelector('.v-the-app-energy-tabs__heading');
          push('tab_click', {
            module: tabsHeading ? txt(tabsHeading) : 'Smarter Living Starts Here',
            button_name: txt(target)
          });
        }
        return;
      }

      /* --- v-faq-tabs: tab_click --- */
      if (target.matches('.v-faq-tabs__nav-btn')) {
        var faqTabsEl = target.closest('v-faq-tabs[data-v-gtm-module]');
        if (faqTabsEl) {
          push('tab_click', {
            module: faqTabsEl.getAttribute('data-v-gtm-module') || '',
            button_name: txt(target)
          });
        }
        return;
      }

      /* --- v-faq-tabs: select_faq (accordion item header) --- */
      if (target.matches('.v-faq-tabs__item-header')) {
        var isExpanding = target.getAttribute('aria-expanded') !== 'true';
        if (isExpanding) {
          var faqTabsEl2 = target.closest('v-faq-tabs[data-v-gtm-module]');
          if (faqTabsEl2) {
            var questionEl = target.querySelector('.v-faq-tabs__item-question');
            push('select_faq', {
              module: faqTabsEl2.getAttribute('data-v-gtm-module') || '',
              button_name: questionEl ? txt(questionEl) : txt(target)
            });
          }
        }
        return;
      }

      /* --- App download badges (#40 / #41) --- */
      if (handleAppDownloadClick(target)) return;

      /* --- Contact Us mailto links (#48) --- */
      if (handleContactUsClick(target)) return;

      /* --- Page-specific banner_click / button_click (templates without gtm_event in JSON) --- */
      var gtmEvent = target.getAttribute('data-v-gtm-event');
      if (!gtmEvent) {
        var body = document.body;

        if (body.classList.contains('template-page-about-us')) {
          var aboutSection = target.closest(
            '.shopify-section--image-with-text-overlay'
          );
          if (aboutSection && isOverlayBannerButton(target)) {
            var aboutBtn = target.closest('a, button') || target;
            handleInferredBannerClick(aboutBtn, aboutSection);
            return;
          }
        }

        if (body.classList.contains('template-page-experience')) {
          var brandCta = target.closest('.v-experience-brands__cta');
          if (brandCta) {
            handleExperienceBrandClick(target);
            return;
          }
          var heroBtn = target.closest('.v-experience-hero__btn');
          if (heroBtn) {
            handleInferredBannerClick(
              heroBtn,
              target.closest('.v-experience-hero')
            );
            return;
          }
        }

        if (body.classList.contains('template-page-influencer-program')) {
          var infSection = target.closest(
            '.shopify-section--image-with-text-overlay'
          );
          if (infSection && isOverlayBannerButton(target)) {
            var infBtn = target.closest('a, button') || target;
            handleInferredBannerClick(infBtn, infSection);
            return;
          }
        }

        if (isHealthBenefitsPage()) {
          var healthSection = target.closest(
            '.shopify-section--image-with-text-overlay'
          );
          if (healthSection && isOverlayBannerButton(target)) {
            var healthBtn = target.closest('a, button') || target;
            handleHealthBenefitBannerClick(healthBtn, healthSection);
            return;
          }
        }
      }

      /* --- Generic data-v-gtm-event attribute (extensibility hook) --- */
      if (gtmEvent) {
        var params = {};
        var attrs = target.attributes;
        for (var i = 0; i < attrs.length; i++) {
          var name = attrs[i].name;
          if (name.indexOf('data-v-gtm-') === 0 && name !== 'data-v-gtm-event') {
            params[name.slice(11).replace(/-/g, '_')] = attrs[i].value;
          }
        }
        /* inherit module from closest ancestor with data-v-gtm-module if not set on element */
        if (!params['module']) {
          var moduleAncestor = target.closest('[data-v-gtm-module]');
          if (moduleAncestor) params['module'] = moduleAncestor.getAttribute('data-v-gtm-module');
        }
        push(gtmEvent, params);
      }
    },
    true
  );

  /* ================================================================
     FAQ — select_faq (#14 / #27)
     ================================================================ */

  document.addEventListener('toggle', function (e) {
    var details = e.target;
    if (!details || details.tagName !== 'DETAILS') return;
    if (!details.open) return;

    var container = details.closest('[data-v-gtm-faq]');
    if (!container) return;

    var summary = details.querySelector(':scope > summary');
    var buttonName = summary
      ? (summary.getAttribute('data-v-gtm-text') || summary.textContent || '').trim().replace(/\s+/g, ' ')
      : '';

    push('select_faq', {
      module: container.getAttribute('data-v-gtm-module') || '',
      button_name: buttonName
    });
  }, true);

  /* ================================================================
     Influencer form_submit (#54) — AJAX success via DOM observer
     ================================================================ */

  function bindInfluencerFormSubmit() {
    document
      .querySelectorAll('[data-v-gtm-form-submit]:not([data-v-gtm-bound])')
      .forEach(function (wrapper) {
        wrapper.setAttribute('data-v-gtm-bound', '1');
        var success = wrapper.querySelector('[data-success-state]');
        if (!success) return;

        var wasVisible =
          !success.hidden && success.classList.contains('is-visible');

        var observer = new MutationObserver(function () {
          var isVisible =
            !success.hidden && success.classList.contains('is-visible');
          if (isVisible && !wasVisible) {
            var headingEl = wrapper.querySelector(
              '[data-form-heading], .v-influencer-form__section-heading'
            );
            var submitBtn = wrapper.querySelector(
              '.v-influencer-form__submit, [type="submit"]'
            );
            push('form_submit', {
              form_name:
                wrapper.getAttribute('data-v-gtm-form-name') ||
                (headingEl ? txt(headingEl) : 'Influencer Application Form'),
              form_submit_text: submitBtn ? txt(submitBtn) : 'Submit'
            });
          }
          wasVisible = isVisible;
        });

        observer.observe(success, {
          attributes: true,
          attributeFilter: ['class', 'hidden']
        });
      });
  }

  /* ================================================================
     Newsroom search (#55) — debounced input on hero + list
     ================================================================ */

  var newsroomSearchTimers = new WeakMap();
  var NEWSROOM_SEARCH_DEBOUNCE_MS = 200;

  function bindNewsroomSearchInput(input) {
    if (!input || input.dataset.vGtmSearchBound) return;
    input.dataset.vGtmSearchBound = '1';

    input.addEventListener('input', function () {
      var existing = newsroomSearchTimers.get(input);
      if (existing) clearTimeout(existing);
      newsroomSearchTimers.set(
        input,
        setTimeout(function () {
          newsroomSearchTimers.delete(input);
          var term = (input.value || '').trim();
          if (!term) return;
          push('search', {
            module: input.getAttribute('data-v-gtm-module') || 'Newsroom Search',
            search_term: term
          });
        }, NEWSROOM_SEARCH_DEBOUNCE_MS)
      );
    });
  }

  function bindNewsroomSearch() {
    document
      .querySelectorAll(
        '.v-newsroom-hero__search-input, .v-newsroom-list__search-input'
      )
      .forEach(bindNewsroomSearchInput);
  }

  document.addEventListener('DOMContentLoaded', function () {
    bindInfluencerFormSubmit();
    bindNewsroomSearch();
  });
  document.addEventListener('shopify:section:load', function () {
    bindInfluencerFormSubmit();
    bindNewsroomSearch();
  });

  /* ================================================================
     Init
     ================================================================ */

  if (document.readyState !== 'loading') {
    bindInfluencerFormSubmit();
    bindNewsroomSearch();
  }

  if (debug) console.log('[vGtm] Initialized — events module loaded');
})();
